<script setup>

import { inject, ref, computed, watch } from 'vue';
import { 
	apiCalculer,
	apiReinitialiserComparaison,
	apiDeclencherRoutine,
	apiExporterJeuxDonnees } from '@/services/ServiceModele.js';
import PanelCoordJeu from './PanelCoordJeu.vue'
import Compteurs from './Compteurs.vue'
import ModaleImportFichier from '@/components/modales/ModaleImportFichier.vue';
import ModaleSourcesDonnees from '@/components/modales/ModaleSourcesDonnees.vue';
import ModaleJeuxDonnees from '@/components/modales/ModaleJeuxDonnees.vue';
import ModaleDeclenchementRoutine from '@/components/modales/ModaleDeclenchementRoutine.vue';

const schema = inject('schema');
const evenement = inject('evenement');
const afficherBarreFlottante = inject('afficherBarreFlottante');
const appelerApiModifModele = inject('appelerApiModifModele');
const appelerApiTelechargementModele = inject('appelerApiTelechargementModele');
const ouvrirJeuDonnees = inject('ouvrirJeuDonnees');

const { donnees } = defineProps({
  donnees: Object
})

const calculEnCours = ref(false);
const boutonMenu = ref(null);
const affichageModaleImportFichier = ref(false);
const affichageModaleSourcesDonnees = ref(false);
const affichageModaleJeuxDonnees = ref(false);
const affichageModaleDeclenchementRoutine = ref(false);
const propositionImport = ref(null);
const routineADeclencher = ref(null);

const itemsMenu = computed(() => {
	return [
		{
			libelle: 'Importer un fichier',
			icones: ['fa fa-upload'],
			groupe: 1,
			commande: () => {
				affichageModaleImportFichier.value = true;
			}
		},
		{
			libelle: 'Déclencher une routine',
			icones: ['fa fa-gear'],
			groupe: 1,
			desactive: itemsMenuRoutines.value.length === 0,
			items: itemsMenuRoutines.value
		},
		{
			libelle: 'Voir les sources de ce jeu de données',
			icones: ['fa fa-arrow-right-to-bracket'],
			groupe: 2,
			commande: () => {
				affichageModaleSourcesDonnees.value = true;
			}
		},
		{
			libelle: 'Exporter les données',
			icones: ['fa fa-download'],
			groupe: 2,
			commande: () => {
				exporterDonnees();
			}
		},
		{
			libelle: 'Voir tous les jeux de données',
			icones: ['fa fa-database'],
			groupe: 3,
			commande: () => {
				affichageModaleJeuxDonnees.value = true;
			}
		}
	];
});

const itemsMenuRoutines = computed(() => {
	return schema.value.routines.map(routine => {
		return {
			libelle: routine.libelle,
			desactive: routine.formuleEnErreur,
			commande: () => {
				afficherModaleDeclenchementRoutine(routine);
			}
		};
	});
});

watch(() => evenement.value.affichageModaleImportFichier, (eventImport) => {
    if (eventImport) {
		propositionImport.value = eventImport;
		delete evenement.value.affichageModaleImportFichier;
		affichageModaleImportFichier.value = true;
	}
});

function basculerAffichageMenu() {
    afficherBarreFlottante({
        items: itemsMenu.value, 
        selectionItems: [],
        composantParent: boutonMenu.value
    });
}

function afficherModaleDeclenchementRoutine(routine) {
	routineADeclencher.value = routine;
	affichageModaleDeclenchementRoutine.value = true;
}

function calculer() {
	calculEnCours.value = true;
	appelerApiModifModele(apiCalculer, () => {
		calculEnCours.value = false;
	});
}

function reinitialiserComparaison() {
	appelerApiModifModele(apiReinitialiserComparaison);
}

function declencherRoutine(routine, textesValeursArgs) {
	appelerApiModifModele(apiDeclencherRoutine, routine.id, textesValeursArgs, (reponseOK) => {
		if (reponseOK) {
			affichageModaleDeclenchementRoutine.value = false;
		}
	});
}

function exporterDonnees() {
	appelerApiTelechargementModele(apiExporterJeuxDonnees, [donnees.jeuDonneesCourant]);
}

</script>

<template>
	<div>
		<label class="titre">Jeu de données</label>
		<div class="panel-jeu-donnees">
			<PanelCoordJeu 
				:jeu-donnees-courant="donnees.jeuDonneesCourant"
				@changement-jeu-donnees="ouvrirJeuDonnees" />
			<div class="etat-donnees">
				<Compteurs :donnees="donnees" />
				<div class="boutons-jeu-donnees">
					<button class="bouton btn-calculer"
						v-if="donnees.jeuDonneesCourant.statut === 'A_CALCULER'"
						:disabled="calculEnCours"
						v-tooltip.left="'Recalculer'"
						@click="calculer()">
						<i :class="['fa-solid', 'fa-refresh', {rotation: calculEnCours}]" />
					</button>
					<button class="bouton btn-effacer-comparaison"
						v-else-if="donnees.modifications.champsValeursModifiees.length || donnees.modifications.champsARecalculer.length"
						v-tooltip.left="'Effacer la comparaison'"
						@click="reinitialiserComparaison()">
						<i class="fa-solid fa-broom" />
					</button>
					<button class="bouton fa-solid fa-bars" 
						ref="boutonMenu"
						@click.stop="basculerAffichageMenu" />
				</div>
			</div>
		</div>
		<ModaleImportFichier 
		  v-model:visible="affichageModaleImportFichier"
		  :proposition-import="propositionImport" />
		<ModaleSourcesDonnees 
		  v-model:visible="affichageModaleSourcesDonnees" />
		<ModaleJeuxDonnees 
		  v-model:visible="affichageModaleJeuxDonnees"
		  :donnees="donnees" />
		<ModaleDeclenchementRoutine 
		  v-model:visible="affichageModaleDeclenchementRoutine"
		  :routine="routineADeclencher"
		  @declenchement="declencherRoutine" />
	</div>
</template>

<style scoped>

.panel-jeu-donnees {
	display: flex;
	flex-direction: column;
	margin-bottom: 20px;
	padding: 5px;
	border: 1px solid var(--couleur-bordure);
	border-radius: 4px;
}

.etat-donnees {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
}

.btn-calculer {
	margin-right: 5px;
}

.rotation {
	animation: spin-animation 0.5s infinite;
}

@keyframes spin-animation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}

.boutons-jeu-donnees {
	display: flex;
}

.bouton {
	height: 20px;
}

</style>

